import * as React from 'react'
import { Tabs as TabsPrimitive } from 'radix-ui'

import { cn } from '@/lib/utils'

function Tabs({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn('flex flex-col', className)}
      {...props}
    />
  )
}

// The only chrome the primitive still owns. Unset means no chrome: the
// consumer styles its own triggers (Duties, DocumentsList). The `pill` variant
// left with its single consumer, the app tab shelf, which now owns that
// styling itself.
type TabsVariant = 'line'

// Set once on the list rather than on every trigger. Styling the triggers from
// the list with descendant selectors would out-specify a trigger's own
// className, which consumers rely on to override the active state.
const TabsListContext = React.createContext<TabsVariant | undefined>(undefined)

function TabsList({
  className,
  variant,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> & {
  variant?: TabsVariant
}) {
  return (
    <TabsListContext.Provider value={variant}>
      <TabsPrimitive.List
        data-slot="tabs-list"
        className={cn(
          // Flex row that respects container width so tabs can shrink/truncate
          'flex w-full min-w-0 h-fit items-center justify-start gap-0 bg-transparent',
          className
        )}
        {...props}
      />
    </TabsListContext.Provider>
  )
}

function TabsTrigger({
  className,
  children,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  const variant = React.useContext(TabsListContext)
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        'group relative inline-flex items-center gap-2 text-sm whitespace-nowrap transition-[color,background-color,border-color,box-shadow,opacity]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-solid focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-50',
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-[18px]",
        variant === 'line' &&
          cn(
            // Underline indicator on the active tab; transparent label otherwise.
            'h-auto min-w-[52px] px-2 py-3',
            'data-[state=inactive]:text-neutral-low-contrast hover:text-primary-high-contrast data-[state=active]:text-primary-high-contrast data-[state=active]:font-medium',
            'after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-primary-solid after:opacity-0 data-[state=active]:after:opacity-100'
          ),
        className
      )}
      {...props}>
      <span className="truncate max-w-full min-w-[10px]">{children}</span>
    </TabsPrimitive.Trigger>
  )
}

/**
 * Unmounts inactive panels (plain Radix). The primitive used to force-mount
 * every panel; the flipped default is API honesty rather than a behavior
 * change — the panels that must keep their state across a tab switch say so
 * with `forceMount` at the call site. The styling below hides them either way.
 */
function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn(
        'outline-none',
        'data-[state=active]:flex data-[state=active]:flex-1 data-[state=active]:min-h-0',
        'data-[state=inactive]:hidden',
        className
      )}
      {...props}
    />
  )
}

export { Tabs, TabsList, TabsTrigger, TabsContent }
